/**
 * Manufacture dates for appliance labels.
 * A date is used only when a printed date is explicit, or one known
 * brand format fits. Anything else stays unclear. No invented year.
 */

const LABEL_KEYS = [
  'brand',
  'model',
  'serial',
  'dateCode',
  'printedDate',
  'capacity',
  'fuelOrVoltage',
];

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const MONTH_INDEX = {
  jan: 1,
  feb: 2,
  mar: 3,
  apr: 4,
  may: 5,
  jun: 6,
  jul: 7,
  aug: 8,
  sep: 9,
  oct: 10,
  nov: 11,
  dec: 12,
};

const LETTER_MONTH = {
  A: 1, B: 2, C: 3, D: 4, E: 5, F: 6, G: 7, H: 8, J: 9, K: 10, L: 11, M: 12,
};

// Bradford White's published chart. The year letter repeats every 20 years.
const BRADFORD_YEARS = {
  A: [2004, 2024],
  B: [2005, 2025],
  C: [2006, 2026],
  D: [2007, 2027],
  E: [2008, 2028],
  F: [2009, 2029],
  G: [2010, 2030],
  H: [2011, 2031],
  J: [2012, 2032],
  K: [2013, 2033],
  L: [2014, 2034],
  M: [2015, 2035],
  N: [2016, 2036],
  P: [2017, 2037],
  S: [2018, 2038],
  T: [2019, 2039],
  W: [2020, 2040],
  X: [2021, 2041],
  Y: [2022, 2042],
  Z: [2023, 2043],
};

const RHEEM_PREFIXES = ['RHNG', 'RHLN', 'RHUN', 'RNG', 'RH', 'RU', 'RN', 'R'];

function todayInNewYork(now) {
  const date = now instanceof Date ? now : new Date(now || Date.now());
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const read = (type) => {
    const found = parts.find((part) => part.type === type);
    return found ? found.value : '';
  };
  const year = Number(read('year'));
  const month = Number(read('month'));
  const day = Number(read('day'));
  const long = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
  return {
    year,
    month,
    day,
    long,
    iso: `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`,
  };
}

function cleanField(value, max) {
  const text = String(value == null ? '' : value).replace(/\s+/g, ' ').trim();
  if (!text || /^not visible$/i.test(text)) return '';
  return text.slice(0, max || 80);
}

function sanitizeLabel(label) {
  if (!label || typeof label !== 'object' || Array.isArray(label)) return null;
  const out = {};
  for (let index = 0; index < LABEL_KEYS.length; index += 1) {
    const key = LABEL_KEYS[index];
    const value = cleanField(label[key], 80);
    if (value) out[key] = value;
  }
  return Object.keys(out).length ? out : null;
}

/** Saved on the thread. The model still receives sanitizeLabel only. */
function persistLabel(label) {
  const safe = sanitizeLabel(label) || {};
  const made = cleanField(label && label.manufactureLabel, 40);
  if (made) safe.manufactureLabel = made;
  return Object.keys(safe).length ? safe : null;
}

function latestApplianceLabel(messages) {
  const list = Array.isArray(messages) ? messages : [];
  for (let index = list.length - 1; index >= 0; index -= 1) {
    const msg = list[index];
    if (!msg || msg.error) continue;
    const label = sanitizeLabel(msg.label);
    if (label) return label;
  }
  return null;
}

function labelFromRequest(bodyLabel, history) {
  return sanitizeLabel(bodyLabel) || latestApplianceLabel(history);
}

function expandYear(yy, currentYear) {
  const year = Number(yy);
  if (!Number.isFinite(year) || year < 0 || year > 99) return 0;
  const currentYY = currentYear % 100;
  return year <= currentYY ? 2000 + year : 1900 + year;
}

function inRange(year, currentYear) {
  return year >= 1980 && year <= currentYear;
}

function monthFromWeek(year, week) {
  const dayOfYear = (week - 1) * 7 + 3;
  const date = new Date(Date.UTC(year, 0, dayOfYear));
  return date.getUTCMonth() + 1;
}

function ageParts(year, month, today) {
  let months = (today.year - year) * 12 + (today.month - month);
  if (today.day < 1) months -= 1;
  if (months < 0) months = 0;
  return {
    years: Math.floor(months / 12),
    months: months % 12,
  };
}

function finishDate(spec, today) {
  const month = spec.month || 0;
  const age = ageParts(spec.year, month || 7, today);
  const about = age.months >= 6 ? age.years + 1 : age.years;
  const ageLabel = `about ${about} ${about === 1 ? 'year' : 'years'}`;
  const labelText = month ? `${MONTHS[month - 1]} ${spec.year}` : String(spec.year);
  const iso = month
    ? `${spec.year}-${String(month).padStart(2, '0')}`
    : String(spec.year);
  const sentence = month
    ? `It was made in ${labelText}. As of ${today.long}, it is ${ageLabel} old.`
    : `The label shows ${spec.year}. As of ${today.long}, it is ${ageLabel} old.`;
  return {
    confident: true,
    lookedUp: true,
    year: spec.year,
    month: month || null,
    week: spec.week || null,
    labelText,
    iso,
    ageLabel,
    sentence,
    source: spec.source,
    todayYear: today.year,
    todayLong: today.long,
  };
}

function unclearResult(reason, today) {
  return {
    confident: false,
    lookedUp: true,
    reason,
    todayYear: today ? today.year : null,
    todayLong: today ? today.long : '',
    unclearSentence: "I can't tell the manufacture date from this label. The date code is unclear, so use the manufacturer's lookup or ask a plumber. I don't want to guess a year.",
  };
}

function monthNameIndex(name) {
  const key = String(name || '').toLowerCase().slice(0, 3);
  return MONTH_INDEX[key] || 0;
}

function parsePrintedDate(value, currentYear) {
  const text = String(value || '');
  if (!text.trim()) return null;
  const named = text.match(/\b(jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:t(?:ember)?)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\.?\s+(\d{4})\b/i);
  if (named) {
    const year = Number(named[2]);
    const month = monthNameIndex(named[1]);
    if (!month || !inRange(year, currentYear)) return { invalid: true };
    return { year, month, source: 'printed' };
  }
  const numeric = text.match(/\b(0?[1-9]|1[0-2])\s*[\/\-]\s*((?:19|20)\d{2})\b/);
  if (numeric) {
    const year = Number(numeric[2]);
    const month = Number(numeric[1]);
    if (!inRange(year, currentYear)) return { invalid: true };
    return { year, month, source: 'printed' };
  }
  const iso = text.match(/\b((?:19|20)\d{2})-(0[1-9]|1[0-2])\b/);
  if (iso) {
    const year = Number(iso[1]);
    const month = Number(iso[2]);
    if (!inRange(year, currentYear)) return { invalid: true };
    return { year, month, source: 'printed' };
  }
  if (/^(?:19|20)\d{2}$/.test(text.trim())) {
    const year = Number(text.trim());
    if (!inRange(year, currentYear)) return { invalid: true };
    return { year, month: null, source: 'printed-year' };
  }
  return null;
}

function cleanSerial(value) {
  return String(value || '').toUpperCase().replace(/[^A-Z0-9.\-]/g, '');
}

function collectSerials(values) {
  const found = [];
  const patterns = [
    /\b\d{4}[A-Z][A-Z0-9]{4,}\b/g,
    /\b(?:RHNG|RHLN|RHUN|RNG|RH|RU|RN)\d{2}[.\-]?\d{2}[A-Z0-9]+\b/g,
    /\b[A-Z]\d{4}[A-Z][A-Z0-9]{3,}\b/g,
    /\b[A-Z]{2}\d{7,8}\b/g,
    /\b\d{2}\.\d{2}-\d{4,}\b/g,
    /\b\d{4}[A-HJ-M]\d{4,}\b/g,
    /\b[A-Z]\d{8,}\b/g,
  ];
  for (let index = 0; index < values.length; index += 1) {
    const text = String(values[index] || '');
    const trimmed = text.trim();
    if (trimmed && trimmed.length <= 24) {
      const one = cleanSerial(trimmed);
      if (one.length >= 6 && /\d/.test(one)) found.push(one);
    }
    for (let pattern = 0; pattern < patterns.length; pattern += 1) {
      const matches = text.toUpperCase().match(patterns[pattern]) || [];
      for (let match = 0; match < matches.length; match += 1) {
        found.push(cleanSerial(matches[match]));
      }
    }
  }
  const unique = [];
  for (let index = 0; index < found.length; index += 1) {
    if (found[index] && unique.indexOf(found[index]) === -1) unique.push(found[index]);
  }
  return unique;
}

function detectBrands(text) {
  const raw = String(text || '');
  const withoutStandard = raw.replace(/american\s+standard/ig, ' ');
  return {
    aoSmith: /a\.?\s*o\.?\s*smith/i.test(raw)
      || /\bstate\b/i.test(raw)
      || /\breliance\b/i.test(raw)
      || /craftmaster/i.test(raw)
      || /\bamerican water heater\b/i.test(raw)
      || (/\bamerican\b/i.test(withoutStandard) && /\bwater heater\b/i.test(raw)),
    rheem: /\brheem\b|\bruud\b|\brichmond\b/i.test(raw),
    bradford: /bradford\s*white/i.test(raw),
    carrier: /\bcarrier\b|\bbryant\b|\bpayne\b/i.test(raw),
    trane: /\btrane\b|american\s+standard/i.test(raw),
    lennox: /\blennox\b/i.test(raw),
    ge: /\bge\b|\bhotpoint\b|\bg\.e\./i.test(raw),
    whirlpool: /\bwhirlpool\b|\bmaytag\b|\bkitchenaid\b/i.test(raw),
  };
}

function anyBrand(brands) {
  return brands.aoSmith || brands.rheem || brands.bradford || brands.carrier
    || brands.trane || brands.lennox || brands.ge || brands.whirlpool;
}

function decodeYearWeek(serial, currentYear) {
  const body = /^[A-Z]\d{4}/.test(serial) ? serial.slice(1) : serial;
  const match = body.match(/^(\d{2})(\d{2})/);
  if (!match) return null;
  const year = expandYear(match[1], currentYear);
  const week = Number(match[2]);
  if (week < 1 || week > 53 || !inRange(year, currentYear)) return null;
  return {
    year,
    month: monthFromWeek(year, week),
    week,
    source: 'ao-smith-yyww',
  };
}

function decodeWeekYear(serial, currentYear) {
  const match = serial.match(/^(\d{2})(\d{2})/);
  if (!match) return null;
  const week = Number(match[1]);
  const year = expandYear(match[2], currentYear);
  if (week < 1 || week > 53 || !inRange(year, currentYear)) return null;
  return {
    year,
    month: monthFromWeek(year, week),
    week,
    source: 'carrier-wwyy',
  };
}

function decodeRheem(serial, currentYear) {
  const tankless = serial.match(/^(\d{2})\.(\d{2})-/);
  if (tankless) {
    const year = expandYear(tankless[1], currentYear);
    const month = Number(tankless[2]);
    if (month >= 1 && month <= 12 && inRange(year, currentYear)) {
      return { year, month, source: 'rheem-yymm' };
    }
    return { ambiguous: true };
  }
  let prefix = '';
  let rest = serial;
  for (let index = 0; index < RHEEM_PREFIXES.length; index += 1) {
    const candidate = RHEEM_PREFIXES[index];
    if (serial.indexOf(candidate) === 0) {
      prefix = candidate;
      rest = serial.slice(candidate.length);
      break;
    }
  }
  const style7 = rest.match(/^[A-Z](\d{2})(\d{2})/);
  if (prefix && style7) {
    const week = Number(style7[1]);
    const year = expandYear(style7[2], currentYear);
    if (week >= 13 && week <= 53 && inRange(year, currentYear)) {
      return { year, month: monthFromWeek(year, week), week, source: 'rheem-wwyy' };
    }
  }
  const digits = rest.match(/^(\d{2})(\d{2})/);
  if (!digits) return null;
  const first = Number(digits[1]);
  const year = expandYear(digits[2], currentYear);
  if (!inRange(year, currentYear)) return null;
  if (prefix && first >= 1 && first <= 12) {
    return { year, month: first, source: 'rheem-mmyy' };
  }
  if (first >= 1 && first <= 53 && (first > 12 || !prefix)) {
    return { year, month: monthFromWeek(year, first), week: first, source: 'rheem-wwyy' };
  }
  return null;
}

function decodeBradford(serial, currentYear) {
  const match = serial.match(/^([A-Z])([A-Z])\d{7,8}$/);
  if (!match) return null;
  const month = LETTER_MONTH[match[2]];
  const years = (BRADFORD_YEARS[match[1]] || []).filter((year) => year <= currentYear && year >= 1980);
  if (!month || !years.length) return null;
  if (years.length > 1) return { ambiguous: true };
  return { year: years[0], month, source: 'bradford-white' };
}

function decodeTrane(serial, currentYear) {
  if (!/^\d/.test(serial)) return null;
  const candidates = [];
  const four = serial.match(/^(\d{2})(\d{2})/);
  if (four) {
    const year = expandYear(four[1], currentYear);
    const week = Number(four[2]);
    if (week >= 1 && week <= 53 && year >= 2010 && year <= currentYear) {
      candidates.push({
        year,
        month: monthFromWeek(year, week),
        week,
        source: 'trane-yyww',
      });
    }
  }
  const digit = Number(serial[0]);
  const week = Number(serial.slice(1, 3));
  if (digit >= 2 && digit <= 9 && week >= 1 && week <= 53) {
    const year = 2000 + digit;
    if (year >= 2002 && year <= 2009 && year <= currentYear) {
      candidates.push({
        year,
        month: monthFromWeek(year, week),
        week,
        source: 'trane-digit',
      });
    }
  }
  if (candidates.length === 1) return candidates[0];
  if (candidates.length > 1) return { ambiguous: true };
  return null;
}

function decodeLennox(serial, currentYear) {
  const match = serial.match(/^(\d{2})(\d{2})([A-HJ-M])/);
  if (!match) return null;
  const year = expandYear(match[2], currentYear);
  const month = LETTER_MONTH[match[3]];
  if (!month || !inRange(year, currentYear)) return null;
  return { year, month, source: 'lennox' };
}

function repeatingCode(serial, brands) {
  if (brands.ge && /^[A-Z]{2}\d{6,}/.test(serial)) return true;
  if (brands.whirlpool && /^[A-Z]\d{8,}/.test(serial)) return true;
  return false;
}

function genericFour(serial, currentYear) {
  if (!/^\d{4}[A-Z][A-Z0-9]{4,}$/.test(serial)) return null;
  const yearWeek = decodeYearWeek(serial, currentYear);
  const weekYear = decodeWeekYear(serial, currentYear);
  if (yearWeek && !weekYear) return yearWeek;
  if (weekYear && !yearWeek) return weekYear;
  if (yearWeek && weekYear) return { ambiguous: true };
  return null;
}

function decodeSerial(serial, brands, currentYear) {
  if (repeatingCode(serial, brands)) return { ambiguous: true };
  const hits = [];
  let ambiguous = false;
  const consider = (result) => {
    if (!result) return;
    if (result.ambiguous) ambiguous = true;
    else if (result.year) hits.push(result);
  };
  if (brands.aoSmith) consider(decodeYearWeek(serial, currentYear));
  if (brands.carrier) consider(decodeWeekYear(serial, currentYear));
  if (brands.rheem) consider(decodeRheem(serial, currentYear));
  if (brands.bradford) consider(decodeBradford(serial, currentYear));
  if (brands.trane) consider(decodeTrane(serial, currentYear));
  if (brands.lennox) consider(decodeLennox(serial, currentYear));
  if (!anyBrand(brands)) consider(genericFour(serial, currentYear));
  if (hits.length === 1 && !ambiguous) return hits[0];
  if (hits.length > 1) {
    const keys = hits.map((hit) => `${hit.year}-${hit.month}`);
    const same = keys.every((key) => key === keys[0]);
    if (same && !ambiguous) return hits[0];
    return { ambiguous: true };
  }
  if (ambiguous) return { ambiguous: true };
  if (anyBrand(brands)) return { miss: true };
  return null;
}

function ageFacts(input) {
  const source = input || {};
  const today = todayInNewYork(source.now);
  const fieldDate = parsePrintedDate(source.printedDate, today.year)
    || parsePrintedDate(source.dateCode, today.year);
  if (fieldDate && fieldDate.invalid) return unclearResult('printed-date', today);
  if (fieldDate && fieldDate.year) return finishDate(fieldDate, today);
  const questionDate = parsePrintedDate(source.question, today.year);
  if (questionDate && questionDate.year && !questionDate.invalid) return finishDate(questionDate, today);

  const brands = detectBrands([source.brand, source.question, source.text].filter(Boolean).join('\n'));
  const serials = collectSerials([
    source.serial,
    source.dateCode,
    source.question,
    source.text,
  ]);
  if (!serials.length) return { confident: false, lookedUp: false };

  const hits = [];
  let ambiguous = false;
  let missed = false;
  for (let index = 0; index < serials.length; index += 1) {
    const decoded = decodeSerial(serials[index], brands, today.year);
    if (!decoded) continue;
    if (decoded.ambiguous) ambiguous = true;
    else if (decoded.miss) missed = true;
    else if (decoded.year) hits.push(decoded);
  }
  if (hits.length === 1 && !ambiguous) return finishDate(hits[0], today);
  if (hits.length > 1) {
    const keys = hits.map((hit) => `${hit.year}-${hit.month}`);
    if (keys.every((key) => key === keys[0]) && !ambiguous) return finishDate(hits[0], today);
  }
  if (ambiguous || missed) return unclearResult('serial', today);
  return { confident: false, lookedUp: false };
}

function assistantSystemPrompt(now) {
  const today = todayInNewYork(now);
  return [
    `Today is ${today.long} in America/New_York. The current year is ${today.year}. Use this date whenever you talk about age. Never say today is a different month or year.`,
    '',
    'Age rules:',
    '- Use a date that is printed on the label.',
    '- Use a brand serial format only when it fits that brand. State and A.O. Smith: the first four digits are YY WW, so 0847 is 2008 week 47 (November 2008), not August 2004.',
    '- Rheem and Ruud: after the letter prefix, month then year. A dotted tankless code is year then month.',
    '- Bradford White: the first letter is the year and the second is the month. The year letter repeats every 20 years. If two years are possible, say the date is unclear.',
    '- Carrier, Bryant, and Payne: the first four digits are WW YY.',
    '- Trane and American Standard: 2010 and later are often YY WW. If a 2002-2009 reading also fits, say the date is unclear.',
    '- Lennox: the third and fourth digits are the year and the next letter is the month.',
    '- GE and Whirlpool year letters or digits repeat. Do not invent a year from those serials.',
    '- Compute the age from today. If you are not sure, say the date is unclear and suggest the manufacturer lookup or a plumber. Never invent a date.',
    '',
    `An install year is a 4-digit year printed on the label, and it cannot be after ${today.year}.`,
  ].join('\n');
}

function labelReadInstructions() {
  return [
    'Read the rating label on any appliance, not only a water heater. Copy the brand, model, serial, manufacture date code, capacity, and fuel or voltage exactly as printed. If a field is not on the label, write "Not visible". Do not guess.',
    'Do not state a manufacture year or an age in the sentences. End your reply with this block and nothing after it:',
    'LABEL',
    'Brand: ',
    'Model: ',
    'Serial: ',
    'Date code: ',
    'Printed date: ',
    'Capacity: ',
    'Fuel or voltage: ',
  ].join('\n');
}

function installYearRule(now) {
  const today = todayInNewYork(now);
  return `For Install Year, only provide a 4-digit year that is printed on the label and is not after ${today.year}. If it is not printed, write "Not visible".`;
}

function ageContextBlock(label, facts, now) {
  const today = todayInNewYork(now);
  const lines = [];
  const safe = sanitizeLabel(label);
  if (safe) {
    lines.push('Label details copied from the photo. Re-check the age from these values and today. Do not keep an earlier guess.');
    if (safe.brand) lines.push(`Brand: ${safe.brand}`);
    if (safe.model) lines.push(`Model: ${safe.model}`);
    if (safe.serial) lines.push(`Serial: ${safe.serial}`);
    if (safe.dateCode) lines.push(`Date code: ${safe.dateCode}`);
    if (safe.printedDate) lines.push(`Printed date: ${safe.printedDate}`);
    if (safe.capacity) lines.push(`Capacity: ${safe.capacity}`);
    if (safe.fuelOrVoltage) lines.push(`Fuel or voltage: ${safe.fuelOrVoltage}`);
  }
  if (facts && facts.confident) {
    lines.push(`Decoded manufacture date: ${facts.labelText}. Age: ${facts.ageLabel} as of ${today.long}. Use this date and no other.`);
  } else if (facts && facts.lookedUp) {
    lines.push('The manufacture date is unclear. Say so, and suggest the manufacturer lookup or a plumber. Do not invent a date.');
  }
  return lines.join('\n');
}

function parseLabelBlock(text) {
  const raw = String(text || '');
  const match = raw.match(/(?:^|\n)LABEL\s*\n([\s\S]*)$/i);
  if (!match) return { prose: raw.trim(), label: null };
  const prose = raw.slice(0, match.index).trim();
  const body = match[1];
  const pick = (name) => {
    const found = body.match(new RegExp(`^${name}:\\s*(.*)$`, 'im'));
    return found ? found[1] : '';
  };
  return {
    prose,
    label: sanitizeLabel({
      brand: pick('Brand'),
      model: pick('Model'),
      serial: pick('Serial'),
      dateCode: pick('Date code'),
      printedDate: pick('Printed date'),
      capacity: pick('Capacity'),
      fuelOrVoltage: pick('Fuel or voltage'),
    }),
  };
}

function sentenceList(text) {
  return String(text || '')
    .split(/(?<=[.!?])\s+/)
    .map((part) => part.trim())
    .filter(Boolean);
}

function conflictingSentence(sentence, facts) {
  if (!facts || !facts.lookedUp) return false;
  const years = sentence.match(/\b(?:19|20)\d{2}\b/g) || [];
  if (/today is/i.test(sentence)) {
    if (facts.todayLong && sentence.indexOf(facts.todayLong) !== -1) return false;
    if (facts.todayYear && sentence.indexOf(String(facts.todayYear)) !== -1 && !years.some((year) => year !== String(facts.todayYear))) {
      return false;
    }
    return true;
  }
  if (facts.confident) {
    const wrongYear = years.some((year) => year !== String(facts.year) && year !== String(facts.todayYear));
    if (wrongYear) return true;
    if (/\byears?\s+old\b/i.test(sentence) && facts.ageLabel && sentence.toLowerCase().indexOf(facts.ageLabel.toLowerCase()) === -1) {
      return true;
    }
    return false;
  }
  if (facts.lookedUp) {
    return years.some((year) => !facts.todayYear || year !== String(facts.todayYear));
  }
  return false;
}

function composeAnswer(text, facts) {
  const raw = String(text || '').trim();
  if (!facts || !facts.lookedUp) return raw;
  const kept = sentenceList(raw).filter((sentence) => !conflictingSentence(sentence, facts));
  const cleaned = kept.join(' ').trim();
  if (facts.confident) {
    const hasWhen = facts.labelText && cleaned.indexOf(facts.labelText) !== -1;
    const hasAge = facts.ageLabel && cleaned.toLowerCase().indexOf(facts.ageLabel.toLowerCase()) !== -1;
    if (hasWhen && hasAge) return cleaned;
    return `${facts.sentence} ${cleaned}`.trim();
  }
  if (/unclear|not sure|can't tell|cannot tell|do not want to guess|don't want to guess/i.test(cleaned)) {
    return cleaned;
  }
  return `${facts.unclearSentence} ${cleaned}`.trim();
}

function labelResponse(label, facts) {
  const safe = sanitizeLabel(label) || {};
  if (facts && facts.confident) {
    safe.manufactureDate = facts.iso;
    safe.manufactureLabel = facts.labelText;
  }
  return Object.keys(safe).length ? safe : null;
}

module.exports = {
  todayInNewYork,
  sanitizeLabel,
  persistLabel,
  latestApplianceLabel,
  labelFromRequest,
  ageFacts,
  assistantSystemPrompt,
  labelReadInstructions,
  installYearRule,
  ageContextBlock,
  parseLabelBlock,
  composeAnswer,
  labelResponse,
  monthFromWeek,
};
